// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductLineComponentModel } from "@store/Library/Components/ProductLine/ProductLine";
import ProductLinePrice, { ProductLinePriceProps } from "@store/Library/Components/ProductLine/ProductLinePrice";
import ProductLineQuantity, {
    ProductLineQuantityProps,
} from "@store/Library/Components/ProductLine/ProductLineQuantity";
import ProductLineSubtotal from "@store/Library/Components/ProductLine/ProductLineSubtotal";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC } from "react";
import { css } from "styled-components";

const hasQuantity = (x: number | undefined | null) => typeof x === "number";
const maxWidth = pxToRem(140);
const gap = pxToRem(16);

export interface ProductLineInformationsProps {
    deliveredAmountProps?: Omit<ProductLinePriceProps, "productLine">;
    priceProps?: Omit<ProductLinePriceProps, "productLine">;
    productLine?: ProductLineComponentModel;
    quantityOrderedProps?: ProductLineQuantityProps;
    quantityRemainingProps?: ProductLineQuantityProps;
    quantityShippedProps?: ProductLineQuantityProps;
    showDeliveredAmount?: boolean;
    showPrice?: boolean;
    showQuantity?: boolean;
    showQuantityRemaining?: boolean;
    showQuantityShipped?: boolean;
    showSubtotal?: boolean;
}

export interface ProductLineInformationsStyles {
    wrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
}

export const productLineInformationsStyles: ProductLineInformationsStyles = {
    wrapper: {
        css: css`
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(max(${maxWidth}, calc((100% / 3) - ${gap})), 1fr));
            gap: ${gap};
            padding-top: ${gap};
            margin-top: ${gap};
            border-top: 1px solid ${getColor("common.borderLight")};
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-grow: 1;
            max-width: ${maxWidth};
        `,
    },
};

const styles = productLineInformationsStyles;

const ProductLineInformations: FC<ProductLineInformationsProps> = ({
    deliveredAmountProps,
    priceProps,
    productLine,
    quantityOrderedProps,
    quantityRemainingProps,
    quantityShippedProps,
    showDeliveredAmount,
    showPrice,
    showQuantity: baseShowQuantity,
    showQuantityRemaining: baseShowQuantityRemaining,
    showQuantityShipped: baseShowQuantityShipped,
    showSubtotal,
}) => {
    if (!productLine) {
        return null;
    }

    const { qtyOrdered, qtyShipped } = productLine;
    const qtyRemaining = productLine.properties.qtyRemaining ? Number(productLine.properties.qtyRemaining) : undefined;

    const showQuantity = baseShowQuantity && hasQuantity(qtyOrdered);
    const showQuantityShipped = baseShowQuantityShipped && hasQuantity(qtyShipped);
    const showQuantityRemaining = baseShowQuantityRemaining && hasQuantity(qtyRemaining);

    if (
        !showQuantity &&
        !showPrice &&
        !showSubtotal &&
        !showQuantityShipped &&
        !showQuantityRemaining &&
        !showDeliveredAmount
    ) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {showQuantity && (
                <StyledWrapper {...styles.infoWrapper} data-test-selector="product_quantity_ordered">
                    <ProductLineQuantity quantity={qtyOrdered} {...quantityOrderedProps} />
                </StyledWrapper>
            )}
            {showPrice && (
                <StyledWrapper {...styles.infoWrapper} data-test-selector="product_price">
                    <ProductLinePrice productLine={productLine} {...priceProps} />
                </StyledWrapper>
            )}
            {showSubtotal && (
                <StyledWrapper {...styles.infoWrapper} data-test-selector="product_subtotal">
                    <ProductLineSubtotal productLine={productLine} />
                </StyledWrapper>
            )}
            {showQuantityShipped && (
                <StyledWrapper {...styles.infoWrapper} data-test-selector="product_quantity_shipped">
                    <ProductLineQuantity
                        label={translate("QTY Shipped")}
                        quantity={qtyShipped}
                        {...quantityShippedProps}
                    />
                </StyledWrapper>
            )}
            {showQuantityRemaining && (
                <StyledWrapper {...styles.infoWrapper} data-test-selector="product_quantity_remaining">
                    <ProductLineQuantity
                        label={translate("QTY Remaining")}
                        quantity={qtyRemaining}
                        {...quantityRemainingProps}
                    />
                </StyledWrapper>
            )}
            {showDeliveredAmount && (
                <StyledWrapper {...styles.infoWrapper} data-test-selector="product_delivered_amount">
                    <ProductLinePrice productLine={productLine} {...deliveredAmountProps} />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default ProductLineInformations;
